/**
 * Skylite Language Selector Component
 * Copyright © 2024 Enterceptor Corp
 * All Rights Reserved
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Menu,
  MenuItem,
  Typography,
  IconButton,
  Tooltip
} from '@mui/material';
import { useLanguage } from '../contexts/LanguageContext';
import EarthIcon from './EarthIcon';

/**
 * @param {{ variant?: 'fixed' | 'inline'; surface?: 'dark' | 'light' }} [props]
 */
const LanguageSelector = ({ variant = 'fixed', surface = 'dark' }) => {
  const { currentLanguage, changeLanguage, supportedLanguages } = useLanguage();
  const [anchorEl, setAnchorEl] = useState(null);
  const [isBrowserMaximized, setIsBrowserMaximized] = useState(false);
  const open = Boolean(anchorEl);
  const isInline = variant === 'inline';
  const isLight = surface === 'light';

  useEffect(() => {
    if (isInline) return undefined;

    const checkBrowserMaximized = () => {
      const browserWindow = document.querySelector('.window[data-type="browser"][data-maximized="true"]');
      const allBrowserWindows = document.querySelectorAll('.window[data-type="browser"]');
      let maximized = false;

      if (allBrowserWindows.length > 0) {
        const browserWindowElement = allBrowserWindows[0];
        const rect = browserWindowElement.getBoundingClientRect();
        const windowWidth = window.innerWidth;
        const windowHeight = window.innerHeight;
        const widthRatio = rect.width / windowWidth;
        const heightRatio = rect.height / windowHeight;
        maximized = widthRatio > 0.9 && heightRatio > 0.9;
        if (browserWindow) maximized = true;
      }

      setIsBrowserMaximized(maximized);
    };

    checkBrowserMaximized();

    const observer = new MutationObserver(checkBrowserMaximized);
    const windowsContainer = document.querySelector('body');
    if (windowsContainer) {
      observer.observe(windowsContainer, {
        attributes: true,
        subtree: true,
        attributeFilter: ['data-maximized', 'style']
      });
    }

    window.addEventListener('resize', checkBrowserMaximized);
    const interval = setInterval(checkBrowserMaximized, 500);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', checkBrowserMaximized);
      clearInterval(interval);
    };
  }, [isInline]);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (languageCode) => {
    changeLanguage(languageCode);
    handleClose();
  };

  if (!isInline && isBrowserMaximized) {
    return null;
  }

  const getFontSize = (langCode) => {
    switch (langCode) {
      case 'zh':
      case 'ja':
        return '1.4rem';
      case 'ar':
        return '1.3rem';
      case 'es':
        return '1.1rem';
      default:
        return '1rem';
    }
  };

  const iconButtonSx = isInline
    ? {
        width: 32,
        height: 32,
        p: 0.5,
        ...(isLight
          ? {
              color: '#1a1033',
              backgroundColor: 'rgba(255,255,255,0.92)',
              border: '1px solid rgba(0,0,0,0.12)',
              '&:hover': {
                backgroundColor: '#fff',
                borderColor: 'rgba(0,0,0,0.2)'
              }
            }
          : {
              color: 'rgba(232, 247, 255, 0.98)',
              backgroundColor: 'rgba(0, 0, 0, 0.45)',
              border: '1px solid rgba(255, 255, 255, 0.18)',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.62)',
                borderColor: 'rgba(255, 255, 255, 0.28)'
              }
            })
      }
    : {
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        color: 'white',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          transform: 'scale(1.05)'
        },
        transition: 'all 0.2s ease-in-out',
        width: 40,
        height: 40
      };

  const menuPaperSx = isLight
    ? {
        backgroundColor: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        boxShadow: '0 8px 32px rgba(15, 23, 42, 0.15)',
        minWidth: 200,
        mt: isInline ? 0.5 : 1
      }
    : {
        backgroundColor: 'rgba(45, 45, 45, 0.95)',
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
        minWidth: 200,
        mt: 1
      };

  const outerSx = isInline
    ? {
        position: 'relative',
        zIndex: 1,
        display: 'flex',
        alignItems: 'center',
        lineHeight: 0,
        direction: 'ltr'
      }
    : {
        position: 'fixed',
        top: 20,
        right: 50,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        direction: 'ltr',
        visibility: isBrowserMaximized ? 'hidden' : 'visible',
        opacity: isBrowserMaximized ? 0 : 1,
        transition: 'opacity 0.3s ease, visibility 0.3s ease'
      };

  return (
    <Box
      className={isInline ? 'language-selector-inline notranslate' : 'language-selector notranslate'}
      translate="no"
      dir="ltr"
      sx={outerSx}
    >
      <Tooltip
        title={<Box component="span" className="notranslate" translate="no" dir="ltr">Translate</Box>}
        arrow
        PopperProps={{ disablePortal: true }}
        componentsProps={{
          tooltip: { className: 'notranslate', translate: 'no' },
          popper: { className: 'notranslate' }
        }}
      >
        <IconButton
          className="notranslate"
          translate="no"
          dir="ltr"
          onClick={handleClick}
          size={isInline ? 'small' : 'medium'}
          sx={iconButtonSx}
          aria-label="Translate"
        >
          <EarthIcon size={isInline ? 22 : 24} />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        MenuListProps={{
          className: 'notranslate',
          translate: 'no',
          sx: { direction: 'ltr' }
        }}
        PaperProps={{
          className: 'notranslate',
          translate: 'no',
          sx: { ...menuPaperSx, direction: 'ltr' }
        }}
      >
        {Object.entries(supportedLanguages).map(([code, lang]) => (
          <MenuItem
            key={code}
            onClick={() => handleLanguageSelect(code)}
            selected={code === currentLanguage}
            sx={{
              color:
                code === currentLanguage
                  ? isLight
                    ? 'primary.main'
                    : '#90caf9'
                  : isLight
                    ? 'text.primary'
                    : 'white',
              backgroundColor:
                code === currentLanguage
                  ? isLight
                    ? 'rgba(25, 118, 210, 0.08)'
                    : 'rgba(144, 202, 249, 0.1)'
                  : 'transparent',
              '&:hover': {
                backgroundColor: isLight ? 'rgba(0, 0, 0, 0.06)' : 'rgba(255, 255, 255, 0.1)'
              },
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              py: 1.5,
              px: 2,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
          >
            <span style={{ fontSize: '1.2em' }}>{lang.flag}</span>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                minWidth: 0,
                flex: 1
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 500,
                  fontSize: getFontSize(code),
                  fontFamily:
                    code === 'ar'
                      ? 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
                      : code === 'zh' || code === 'ja'
                        ? 'Microsoft YaHei, SimSun, Hiragino Sans GB, sans-serif'
                        : 'inherit',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%'
                }}
              >
                {lang.nativeName}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  opacity: 0.7,
                  fontSize: code === 'zh' || code === 'ja' ? '0.8rem' : '0.75rem',
                  fontFamily:
                    code === 'ar'
                      ? 'Segoe UI, Tahoma, Geneva, Verdana, sans-serif'
                      : code === 'zh' || code === 'ja'
                        ? 'Microsoft YaHei, SimSun, Hiragino Sans GB, sans-serif'
                        : 'inherit',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%'
                }}
              >
                {lang.name}
              </Typography>
            </Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default LanguageSelector;
